// src/components/SimpleComponents/SimpleWebsite.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import ValidatedInput from 'components/JarvislyComponents/ValidatedInput';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleWebsite = ({ name, subdocumentField, disabled, onBlur, prefix, autoFocus, ...props }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus({ ...props, ...elementProps }, isValidWebsite);

  // Read value from Form state so it reacts to typing, paste and programmatic setFieldsValue
  const currentValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const statusForSuffix = useMemo(() => {
    // Empty value => no icon
    if (!currentValue) return '';

    // Show validating if hook flagged it
    if (customValidationStatus === 'validating') return 'validating';

    // When there is a value: error if invalid, success if valid
    return isValidWebsite(currentValue) ? 'success' : 'error';
  }, [currentValue, customValidationStatus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      // Keep AntD feedback off; we use our static suffix
      showFeedback={false}
    >
      <ValidatedInput
        ref={elementRef}
        id={flattenFieldName}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={isDisabled}
        status={statusForSuffix}
        loading={false}
        prefix={prefix}
        autoFocus={autoFocus}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleWebsite;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isValidWebsite(value) {
  if (!value || typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  // Allow URLs with or without protocol
  // Accepts: example.com, www.example.com, https://example.com, http://example.com/path
  const urlPattern =
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i;

  return urlPattern.test(trimmed);
}
